import { wrapCreateBrowserRouterV6 as wrapCreateBrowserRouter } from '@sentry/react'
import { lazy, Suspense } from 'react'
import { useSelector } from 'react-redux'
import {
  createBrowserRouter,
  createRoutesFromElements,
  Navigate,
  Outlet,
  Route,
  RouterProvider,
  useLocation,
} from 'react-router-dom'

import { routes as playersRoutes } from 'players'
import { routes as searchRoutes } from 'search'
import System from 'system'
import { router as tournamentsRouter } from 'tournaments'
import { ProgressBar } from 'ui'

import Site from './layout/Site'

const InstitutionPage = lazy(
  () => import(/* webpackChunkName: 'institutionpage' */ '../pages/institution/InstitutionPage'),
)
const MatchPage = lazy(() => import(/* webpackChunkName: 'matchpage' */ '../pages/sports/soccer/MatchPage'))
const TeamPage = lazy(() => import(/* webpackChunkName: 'teampage' */ '../pages/sports/soccer/TeamPage'))
const TournamentPage = lazy(() => import(/* webpackChunkName: 'tournamentpage' */ '../pages/sports/TournamentPage'))

const UserInstitutions = lazy(() => import(/* webpackChunkName: 'userinstitutions' */ './institutions/list'))
const UserPage = lazy(() => import(/* webpackChunkName: 'userpage' */ '../pages/UserPage'))
const UserNotifications = lazy(
  () => import(/* webpackChunkName: 'usernotifications' */ '../pages/user/UserNotifications'),
)
const UserSettings = lazy(() => import(/* webpackChunkName: 'usersettings' */ '../pages/UserSettings'))
const UserTeams = lazy(() => import(/* webpackChunkName: 'teams' */ 'teams/src/screens/List'))
const UserTournaments = lazy(() => import(/* webpackChunkName: 'usertournaments' */ '../pages/user/UserTournaments'))

const Site404 = lazy(() => import(/* webpackChunkName: 'site404' */ '../pages/site/Site404'))
const AppHome = lazy(() => import(/* webpackChunkName: 'site' */ 'site'))

const Stats4Nerds = lazy(() => import(/* webpackChunkName: 'global' */ '../pages/Stats4Nerds'))
const SweatBitsAdmin = lazy(() => import(/* webpackChunkName: 'global' */ '../pages/SweatBitsAdmin'))

const routesList = [...playersRoutes, ...searchRoutes]

const redirects = [
  { from: '/i/:id/:section?', to: (path: string) => path.replace('/i/', '/institution/') },
  { from: '/institutions/:id/:section?', to: (path: string) => path.replace('/institutions/', '/institution/') },
  { from: '/m/:id/:section?', to: (path: string) => path.replace('/m/', '/sports/soccer/match/') },
  { from: '/privacy', to: (path: string) => path.replace('/privacy', '/platform/privacy') },
  {
    from: '/soccer/match/:id/:section?',
    to: (path: string) => path.replace('/soccer/match/', '/sports/soccer/match/'),
  },
  { from: '/ssm/:id/:section?', to: (path: string) => path.replace('/ssm/', '/sports/soccer/match/') },
  { from: '/sst/:id/:section?', to: (path: string) => path.replace('/sst/', '/sports/soccer/team/') },
  { from: '/st/:id/:section?', to: (path: string) => path.replace('/st/', '/sports/tournament/') },
  { from: '/team/:id/:section?', to: (path: string) => path.replace('/team/', '/sports/soccer/team/') },
  { from: '/u/:id', to: (path: string) => path.replace('/u/', '/user/') },
]

let createRouter = createBrowserRouter

const urlParams = new URLSearchParams(window.location.search)
if (urlParams.get('r')) {
  document.location.replace(`${System.url}${urlParams.get('r')}`)
}

if (process.env.NODE_ENV === 'production' && System.sentry?.enabled) {
  createRouter = wrapCreateBrowserRouter(createBrowserRouter)
}

const AuthAccess = () => {
  const auth = useSelector((state: GlobalState) => state.auth as { loaded: boolean; loading: boolean; usr: unknown })

  const loadingContent = (
    <div className="AuthAccess p-2">
      <ProgressBar value={100} />
    </div>
  )

  if (auth?.loading && !auth?.loaded) {
    return loadingContent
  }

  if (!auth?.usr) {
    return <Site404 />
  }

  return (
    <Suspense fallback={loadingContent}>
      <Outlet />
    </Suspense>
  )
}

const Redirect = ({ to }: { to: (path: string) => string }) => <Navigate to={to(useLocation().pathname)} />

const router = createRouter(
  createRoutesFromElements(
    <Route path="/">
      {redirects.map((red) => (
        <Route key={red.from} element={<Redirect to={red.to} />} path={red.from} />
      ))}

      <Route element={<Site />} path="/">
        <Route element={<AppHome />} index />
        <Route element={<AppHome />} path="/apoya" />
        <Route element={<AppHome />} path="/auth/reset/:token/:code" />
        <Route element={<AppHome />} path="/auth/verify/:token/:code" />
        <Route element={<AppHome />} path="/platform/:section?" />

        <Route element={<InstitutionPage />} path="/institution/:id/:section?" />
        <Route element={<MatchPage />} path="/sports/soccer/match/:id/:section?" />
        <Route element={<Stats4Nerds />} path="/stats4nerds" />
        <Route element={<SweatBitsAdmin />} path="/sweatbits/admin" />
        <Route element={<TeamPage />} path="/sports/soccer/team/:id/:section?" />
        <Route element={<TournamentPage />} path="/sports/tournament/:id/:section?" />
        <Route element={<UserInstitutions />} path="/institutions" />
        <Route element={<UserNotifications />} path="/notifications" />
        <Route element={<UserPage />} path="/user/:id" />
        <Route element={<UserTeams />} path="/teams" />
        <Route element={<UserTournaments />} path="/tournaments" />

        <Route element={<AuthAccess />}>
          <Route element={<UserSettings />} path="/settings" />
        </Route>

        {routesList.map((route) => (
          <Route key={route.path} element={<route.component />} path={route.path} />
        ))}
        {tournamentsRouter()}
      </Route>

      <Route element={<Site404 />} path="*" />
    </Route>,
  ),
  {
    future: {
      v7_fetcherPersist: true,
      v7_normalizeFormMethod: true,
      v7_partialHydration: true,
      v7_relativeSplatPath: true,
      v7_skipActionErrorRevalidation: true,
    },
  },
)

export default () => (
  <RouterProvider
    future={{
      v7_startTransition: true,
    }}
    router={router}
  />
)
